import { FC, memo, useCallback, useRef } from 'react';
// mui
import { debounce, SelectChangeEvent } from '@mui/material';
// components
import SelectController, { ISelectController } from 'controllers/select-controller';
// styles
import { styles } from './styles';
import { ILocationSelect } from './types';

const LocationSelect: FC<ILocationSelect> = ({
  isLocationsFetching,
  name,
  nextCursor,
  options,
  selectedOptionName,
  setSelectedOptionName,
  setLocationsParams,
  resetLocationsParams,
  ...props
}) => {
  const isOpenRef = useRef(null);

  const getSelectedOptionLabel = useCallback(
    (
      selectOptions: ISelectController['options'],
      selectedValue?: ISelectController['options'][number] | number,
      title?: string
    ) => {
      return typeof selectedValue === 'number'
        ? selectOptions?.find(({ value }) => value === selectedValue).label
        : !selectedValue && title
        ? title
        : null;
    },
    []
  );

  const renderSelectValue = useCallback(
    (selectedValue?: ISelectController['options'][number] | number) => {
      return (
        <p>
          {getSelectedOptionLabel(options, selectedValue, selectedOptionName) ?? 'Select Location'}
        </p>
      );
    },
    [getSelectedOptionLabel, options, selectedOptionName]
  );

  const onSearchChange = useCallback<(value?: string) => void>(
    debounce((searchValue?: string) => {
      setLocationsParams((prev) => ({
        ...prev,
        cursor: undefined,
        search: searchValue ? searchValue : undefined,
      }));
    }),
    []
  );

  const onIntersection = useCallback(() => {
    if (nextCursor && isOpenRef.current) {
      setLocationsParams((prev) => ({
        ...prev,
        cursor: nextCursor,
      }));
    }
  }, [nextCursor]);

  const onChange = useCallback(
    (e: SelectChangeEvent<number>) => {
      const foundedOption = options.find(({ value }) => value === e.target.value);

      if (foundedOption) {
        setSelectedOptionName(foundedOption.label, foundedOption.value);
      }
    },
    [setSelectedOptionName, options]
  );

  const onOpen = () => {
    isOpenRef.current = true;
  };

  const onClose = () => {
    resetLocationsParams();
    isOpenRef.current = false;
  };

  return (
    <>
      <SelectController
        onOpen={onOpen}
        fullWidth
        isSelect
        withError
        name={name}
        options={options}
        paperStyles={styles.paperStyles}
        withDataFetching={true}
        autoFocus
        isFetching={isLocationsFetching}
        onIntersection={onIntersection}
        onSearchChange={onSearchChange}
        onClose={onClose}
        onChange={onChange}
        renderValue={renderSelectValue}
        {...props}
      />
    </>
  );
};

export default memo(LocationSelect);
